<script setup lang="ts">
import { type ComponentPublicInstance, computed, shallowRef } from 'vue';
import { store } from '@/app/store';
import { useLegacyCarouselPager } from '@/components/legacy-gallery/useLegacyCarouselPager';
import {
  LEGACY_GALLERY_EMPTY_SELECTION,
  useLegacyGalleryFacade,
} from '@/components/legacy-gallery/useLegacyGalleryFacade';
import { useLegacyGalleryTelemetry } from '@/components/legacy-gallery/useLegacyGalleryTelemetry';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import CustomerPortraitDownloadCardOverlay from './CustomerPortraitDownloadCardOverlay.vue';
import type {
  CustomerPortraitDownloadGalleryApi,
  CustomerPortraitDownloadItem,
} from './customerPortraitDownloadTypes';

interface CustomerPortraitDownloadPage {
  aspectRatioSum: number;
  items: readonly CustomerPortraitDownloadItem[];
  startIndex: number;
  uuid: string;
}

interface Emits {
  download: [portrait: CustomerPortraitDownloadItem];
  imageError: [
    portrait: CustomerPortraitDownloadItem,
    index: number,
    event: Event,
  ];
  imageLoad: [
    portrait: CustomerPortraitDownloadItem,
    index: number,
    event: Event,
  ];
  retry: [index: number];
  share: [portrait: CustomerPortraitDownloadItem, index: number];
}

interface Props {
  actionFontScale?: number;
  greenscreen?: boolean;
  items: readonly CustomerPortraitDownloadItem[];
}

const ITEMS_PER_SLIDE = 3;
const DEFAULT_PORTRAIT_ASPECT_RATIO = 832 / 1216;
const GREENSCREEN_ASPECT_RATIO = 4 / 3;
const PAGES_PER_SLIDE = 1;

const props = withDefaults(defineProps<Props>(), {
  actionFontScale: 0.75,
  greenscreen: false,
});
const emit = defineEmits<Emits>();

const imageElements = shallowRef<Array<HTMLImageElement | null>>([]);
const portraitElements = shallowRef<Array<HTMLElement | null>>([]);
const isVerticalKiosk = computed(() => Boolean(store.isVerticalKiosk));
const viewportIsPortrait = computed(() => Boolean(store.viewportIsPortrait));
const actualItemsPerSlide = computed(() =>
  viewportIsPortrait.value || isVerticalKiosk.value ? 1 : ITEMS_PER_SLIDE
);

function displayAspectRatio(item: CustomerPortraitDownloadItem): number {
  if (item.aspectRatio) {
    return item.aspectRatio;
  }
  return props.greenscreen
    ? GREENSCREEN_ASPECT_RATIO
    : DEFAULT_PORTRAIT_ASPECT_RATIO;
}

const pages = computed<readonly CustomerPortraitDownloadPage[]>(() => {
  const pageSize = actualItemsPerSlide.value;
  const result: CustomerPortraitDownloadPage[] = [];

  for (
    let startIndex = 0;
    startIndex < props.items.length;
    startIndex += pageSize
  ) {
    const pageItems = props.items.slice(startIndex, startIndex + pageSize);
    result.push({
      aspectRatioSum: pageItems.reduce(
        (sum, item) => sum + displayAspectRatio(item),
        0
      ),
      items: pageItems,
      startIndex,
      uuid: `customer-download-page-${pageItems
        .map((item) => item.uuid)
        .join('-')}`,
    });
  }

  return result;
});

const telemetry = useLegacyGalleryTelemetry({
  actualItemsPerSlide,
  isGridMode: false,
  items: () => props.items,
  onImageError: (
    portrait: CustomerPortraitDownloadItem,
    index: number,
    event: Event
  ): void => {
    emit('imageError', portrait, index, event);
  },
  selectedItemUuids: LEGACY_GALLERY_EMPTY_SELECTION,
  selectionMode: null,
});
const pager = useLegacyCarouselPager({
  actualItemsPerSlide: PAGES_PER_SLIDE,
  isGridMode: true,
  items: pages,
  onSwitchSlide: (
    delta: number,
    firstVisiblePageIndex: number,
    visiblePages: Array<CustomerPortraitDownloadPage | undefined>
  ) => {
    const visibleItems = visiblePages[0]?.items ?? [];
    telemetry.recordSlideSwitch({
      delta,
      firstVisibleItemIndex: firstVisiblePageIndex * actualItemsPerSlide.value,
      visibleItems: [...visibleItems],
    });
  },
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function setPortraitElement(
  element: Element | ComponentPublicInstance | null,
  index: number
): void {
  portraitElements.value[index] =
    element instanceof HTMLElement ? element : null;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onItemClick(
  event: MouseEvent,
  item: CustomerPortraitDownloadItem
): void {
  telemetry.recordItemClick(event, item);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onImageLoad(
  item: CustomerPortraitDownloadItem,
  index: number,
  event: Event
): void {
  emit('imageLoad', item, index, event);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onImageError(
  item: CustomerPortraitDownloadItem,
  index: number,
  event: Event
): void {
  telemetry.recordImageLoadError(item, index, event);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function mediaOrientation(
  item: CustomerPortraitDownloadItem
): 'landscape' | 'portrait' {
  return displayAspectRatio(item) > 1 ? 'landscape' : 'portrait';
}

function getImageElement(index: number): HTMLImageElement | null {
  return imageElements.value[index] ?? null;
}

function getCurrentPageIndex(): number {
  return Math.min(
    pager.getFirstVisibleItemIndex(),
    Math.max(pages.value.length - 1, 0)
  );
}

function getFirstVisibleItemIndex(): number {
  return getCurrentPageIndex() * actualItemsPerSlide.value;
}

function getVisibleItemUuids(): Array<string | undefined> {
  return (
    pages.value[getCurrentPageIndex()]?.items.map((item) => item.uuid) ?? []
  );
}

function getPageWidth(): number {
  return pager.scrollable.value?.clientWidth || 1;
}

function isItemVisible(uuid: string): boolean {
  return getVisibleItemUuids().includes(uuid);
}

function scrollToItem(uuid: string): void {
  const page = pages.value.find((candidate) =>
    candidate.items.some((item) => item.uuid === uuid)
  );
  if (page) {
    pager.scrollToItem(page.uuid);
  }
}

const {
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setContainerElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setImageElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setItemElement: setPageElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setScrollableElement,
} = useLegacyGalleryFacade({
  actualItemsPerSlide,
  extras: {
    getFirstVisibleItemIndex,
    getItemWidth: getPageWidth,
    getVisibleItemUuids,
    isItemVisible,
    get isVerticalKiosk(): boolean {
      return isVerticalKiosk.value;
    },
    itemsPerSlide: ITEMS_PER_SLIDE,
    scrollToItem,
    get viewportIsPortrait(): boolean {
      return viewportIsPortrait.value;
    },
    get visibleItemUuids(): Array<string | undefined> {
      return getVisibleItemUuids();
    },
  },
  imageElements,
  itemElements: portraitElements,
  items: () => props.items,
  pager,
});

defineExpose<CustomerPortraitDownloadGalleryApi>({
  getImageElement,
  scrollToItem,
});
</script>

<template>
  <div
    :ref="setContainerElement"
    class="ImageGallery CustomerPortraitDownloadGallery"
    :class="{
      'ImageGallery--arrow-prev-hidden': pager.hidePrevArrow.value,
      'ImageGallery--arrow-next-hidden': pager.hideNextArrow.value,
    }"
    :style="{
      '--customer-download-action-font-scale': actionFontScale,
    }"
  >
    <button
      class="ImageGallery__arrow ImageGallery__arrow--prev"
      type="button"
      aria-label="Previous portraits"
      @click="pager.switchSlide(-1)"
    />
    <button
      class="ImageGallery__arrow ImageGallery__arrow--next"
      type="button"
      aria-label="Next portraits"
      @click="pager.switchSlide(1)"
    />

    <div
      :ref="setScrollableElement"
      class="ImageGallery__scrollable hidden-scrollbar"
    >
      <ol class="ImageGallery__list CustomerPortraitDownloadGallery__pages">
        <li
          v-for="(page, pageIndex) in pages"
          :key="page.uuid"
          :ref="(element) => setPageElement(element, pageIndex)"
          class="CustomerPortraitDownloadGallery__page"
          data-testid="customer-download-gallery-page"
          :style="{
            '--CustomerPortraitDownload--page-aspect-ratio-sum':
              page.aspectRatioSum,
            '--CustomerPortraitDownload--page-item-count': page.items.length,
          }"
        >
          <ul class="CustomerPortraitDownloadGallery__page-items">
            <li
              v-for="(portrait, pageItemIndex) in page.items"
              :key="
                portrait.renderKey ||
                portrait.uuid ||
                `gallery-item-${page.startIndex + pageItemIndex}`
              "
              :ref="
                (element) =>
                  setPortraitElement(
                    element,
                    page.startIndex + pageItemIndex
                  )
              "
              class="ImageGallery__item"
              data-testid="gallery-item"
              data-selected="false"
              :data-orientation="mediaOrientation(portrait)"
              :data-loading="
                portrait.isPreloaded === false && !portrait.isError
                  ? 'true'
                  : 'false'
              "
              :data-error="portrait.isError ? 'true' : 'false'"
              @click="onItemClick($event, portrait)"
            >
              <div
                class="ImageGallery__item-wrapper"
                :style="{
                  '--CustomerPortraitDownload--item-ratio':
                    displayAspectRatio(portrait),
                }"
              >
                <div
                  class="ImageGallery__item-overlay CustomerPortraitsDownloadCode__overlay"
                >
                  <CustomerPortraitDownloadCardOverlay
                    :index="page.startIndex + pageItemIndex"
                    :portrait="portrait"
                    @download="emit('download', $event)"
                    @retry="emit('retry', $event)"
                    @share="
                      (sharedPortrait, sharedIndex) =>
                        emit('share', sharedPortrait, sharedIndex)
                    "
                  />
                </div>

                <img
                  :key="`${portrait.uuid}-${portrait.loadAttempt ?? 0}`"
                  :ref="
                    (element) =>
                      setImageElement(
                        element,
                        page.startIndex + pageItemIndex
                      )
                  "
                  class="ImageGallery__item-image sentry-block"
                  :class="{ 'opacity-0': !portrait.isPreloaded }"
                  :src="portrait.url"
                  :alt="`${$t('CustomerPortraitsDownloadCode.imageAlt')} ${
                    page.startIndex + pageItemIndex + 1
                  }`"
                  @load="
                    onImageLoad(
                      portrait,
                      page.startIndex + pageItemIndex,
                      $event
                    )
                  "
                  @error="
                    onImageError(
                      portrait,
                      page.startIndex + pageItemIndex,
                      $event
                    )
                  "
                />

                <i
                  v-show="false"
                  class="ImageGallery__item-checkmark kiosk-success-icon"
                />
              </div>
            </li>
          </ul>
        </li>
      </ol>
    </div>
  </div>
</template>

<style scoped>
.ImageGallery {
  --CustomerPortraitDownload--default-portrait-ratio: calc(832 / 1216);
  --CustomerPortraitDownload--page-inline-padding: clamp(0rem, 1.4vw, 1.5rem);
  --ImageGallery--arrow-size: clamp(2.5rem, 4.5vw, 4rem);
  --CustomerPortraitDownload--navigation-track-size: calc(
    var(--ImageGallery--arrow-size) + clamp(0.5rem, 1vw, 1rem)
  );
  --ImageGallery--item-width: min(
    var(
      --legacy-gallery-context-item-width,
      var(--legacy-gallery-item-width, 26rem)
    ),
    calc(100vw - (2 * var(--Kiosk--wrapper-side-gap)))
  );

  display: grid;
  grid-template-areas: 'previous gallery next';
  grid-template-columns:
    var(--CustomerPortraitDownload--navigation-track-size)
    minmax(0, 1fr)
    var(--CustomerPortraitDownload--navigation-track-size);
  align-items: center;
  width: 100%;
  max-width: 100%;
}

.ImageGallery__scrollable {
  grid-area: gallery;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  margin-inline: auto;
  padding: 0;
  overflow-x: auto;
  overflow-y: visible;
  scroll-padding-inline: 0;
  scroll-snap-type: x mandatory;
}

.ImageGallery__list {
  display: flex;
  width: 100%;
  padding: 0;
  margin: 0;
  align-items: stretch;
  list-style: none;
}

.CustomerPortraitDownloadGallery__page {
  container-type: inline-size;
  display: grid;
  flex: 0 0 100%;
  width: 100%;
  min-width: 100%;
  padding-inline: var(--CustomerPortraitDownload--page-inline-padding);
  place-items: center;
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

.CustomerPortraitDownloadGallery__page-items {
  --CustomerPortraitDownload--page-gap-total: calc(
    (
        var(--CustomerPortraitDownload--page-item-count, 1) -
          1
      ) *
      var(--ImageGallery--item-gap)
  );
  --CustomerPortraitDownload--card-height-by-width: calc(
    (
        100cqw -
          var(--CustomerPortraitDownload--page-gap-total)
      ) /
      var(--CustomerPortraitDownload--page-aspect-ratio-sum, 1)
  );
  --CustomerPortraitDownload--card-height: min(
    var(--ImageGallery--item-height),
    var(--CustomerPortraitDownload--card-height-by-width)
  );

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  width: 100%;
  padding: 0;
  margin: 0;
  place-content: center;
  gap: var(--ImageGallery--item-gap);
  list-style: none;
}

.ImageGallery__item {
  display: grid;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;
  place-items: center;
  overflow: visible;
  scroll-snap-align: none;
}

.ImageGallery__item-wrapper {
  --ImageGallery--item-radius: 1rem;
  --ImageGallery--text-size: calc(
    var(--CustomerPortraitDownload--card-height) *
      var(--CustomerPortraitDownload--default-portrait-ratio) *
      0.06
  );

  overflow: hidden;
  width: calc(
    var(--CustomerPortraitDownload--card-height) *
      var(--CustomerPortraitDownload--item-ratio)
  );
  height: var(--CustomerPortraitDownload--card-height);
  aspect-ratio: var(
    --CustomerPortraitDownload--item-ratio,
    var(--ImageGallery--item-ratio)
  );
  background: rgba(var(--Kiosk--tint-color-rgb), 0.08);
}

.ImageGallery__item-image {
  object-fit: contain;
}

.ImageGallery__arrow:focus-visible {
  outline: 0.2rem solid var(--Kiosk--font_color);
  outline-offset: 0.2rem;
}

.ImageGallery__arrow {
  position: relative;
  inset: auto;
  justify-self: center;
  align-self: center;
  padding: 0;
}

.ImageGallery__arrow--prev {
  grid-area: previous;
  transform: rotate(180deg);
}

.ImageGallery__arrow--next {
  grid-area: next;
  transform: none;
}

.CustomerPortraitsDownloadCode__overlay {
  display: flex;
  align-items: end;
}
</style>
